import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, Title, Tooltip } from '@xsolla/ui-core';
import { ChevronDownMini } from '@xsolla/ui-icons';

import { Error } from '../../../api/setup/getApiError';
import { User, UsersData } from '../../../api/users/types';
import { ISortStatuses } from '../../../hooks/types';
import { FlexParentBlock, VerticalCenterBlock } from '../../../styled';
import { filterObject } from '../../../utils';
import { Table, VerticalSeparator } from '../../ui';
import { UserData } from '../AttributesModal/types';
import SelectUserSettings from './SelectUserSettings';
import {
  BanInfoDescription,
  BanInfoIndent,
  BanInfoRow,
  BanInfoSubtitle,
  BanInfoTitle,
  BanInfoWrapper,
  BanTitlesBlock,
  ContactInfo,
  ContactWrapper,
  GroupLabel,
  ManageUserWrapper,
  NavCellIcon,
  NavCellWrapper,
  NoDataContentBox,
  NoDataSubtitle,
  NotConfirmStatusCircle,
  UserIdLabel,
  UserInfoWrapper,
  UserMoreButton,
  UsernameLabel
} from './style';

type Props = {
  fetching: boolean;
  tableData?: User[];
  sortStatuses: ISortStatuses;
  setUsersState: React.Dispatch<React.SetStateAction<UsersData>>;
  setUserAttributes: React.Dispatch<React.SetStateAction<UserData>>;
  onOpenUserAttributesModal: () => void;
  sortData: (key: string) => void;
  error: Error | null;
};

type CollapseBanInfo = Record<string, boolean>;

export const UserTable = ({
  fetching,
  tableData,
  setUsersState,
  sortData,
  sortStatuses,
  setUserAttributes,
  onOpenUserAttributesModal,
  error
}: Props): JSX.Element => {
  const intl = useIntl();
  const [collapseBanInfo, setCollapseBanInfo] = useState<Partial<CollapseBanInfo>>({});

  const bannedUsers = [...(tableData || [])]
    .sort((a, b) => (a.id < b.id ? -1 : 1))
    .reduce((acc, user) => (user.ban ? [...acc, user.id] : acc), []);

  useEffect(() => {
    const filteredCollapseBanInfo = filterObject(collapseBanInfo, (key) =>
      bannedUsers.includes(key)
    );
    setCollapseBanInfo(filteredCollapseBanInfo);
  }, [JSON.stringify(bannedUsers)]);

  const tableHeadProps = [
    { width: 5, separator: false },
    {
      width: 29,
      label: intl.messages['users.list-block.table.id-username'],
      name: 'username',
      sortFunc: sortData
    },
    {
      width: 24,
      label: intl.messages['users.list-block.table.contacts'],
      name: 'contact',
      sortFunc: sortData
    },
    {
      width: 12,
      label: intl.messages['users.list-block.table.group'],
      name: 'group',
      sortFunc: sortData
    },
    {
      width: 10,
      label: intl.messages['users.list-block.table.activity'],
      name: 'activity',
      separator: false,
      sortFunc: sortData
    },
    { width: 20, separator: false, name: 'actions' }
  ];

  const getRowStyles = useCallback(
    (props: User) => {
      return props.ban?.date_from ? { backgroundColor: 'rgba(235, 0, 47, 0.04)' } : {};
    },
    [tableData]
  );

  const renderContactInfo = (
    is_last_email_confirmed: boolean,
    email: string,
    phone_auth: string | null
  ) => {
    return (
      <ContactWrapper>
        <VerticalCenterBlock>
          {!is_last_email_confirmed && !!email && (
            <Tooltip content={intl.formatMessage({ id: 'users.list-block.table.contacts.help' })}>
              <NotConfirmStatusCircle />
            </Tooltip>
          )}
          <ContactInfo>{email}</ContactInfo>
        </VerticalCenterBlock>
        <ContactInfo>{phone_auth}</ContactInfo>
      </ContactWrapper>
    );
  };

  const rowComponents = ({
    id,
    username,
    ban,
    email,
    is_last_email_confirmed,
    groups,
    registered,
    external_id,
    last_login,
    phone_auth
  }: User) => ({
    main: [
      ban?.date_from ? (
        <NavCellWrapper
          onClick={() => setCollapseBanInfo((prev) => ({ ...prev, [id]: !prev[id] }))}>
          <NavCellIcon collapsed={collapseBanInfo[id]}>
            <ChevronDownMini />
          </NavCellIcon>
        </NavCellWrapper>
      ) : (
        <div />
      ),
      <UserInfoWrapper>
        <UsernameLabel>{username}</UsernameLabel>
        <UserIdLabel>ID {id}</UserIdLabel>
      </UserInfoWrapper>,
      renderContactInfo(is_last_email_confirmed, email, phone_auth),
      <GroupLabel>
        {groups.map(({ name }, index) => {
          return (
            <div key={name}>
              {name}
              {index === groups.length - 1 ? '' : ', '}
            </div>
          );
        })}
      </GroupLabel>,
      <div>
        {intl.formatMessage(
          { id: `users.list-block.table.${ban?.date_from ? 'ban-date' : 'last-activity'}` },
          {
            date: ban?.date_from
              ? moment(ban.date_from).format('L LT')
              : moment(last_login || registered).fromNow(),
            br: () => <br />
          }
        )}
      </div>,
      <ManageUserWrapper>
        <Button
          look="link"
          palette="brand"
          size="md"
          onClick={() => {
            setUserAttributes({ id, username });
            onOpenUserAttributesModal();
          }}
          data-test-id="user-attributes">
          {intl.formatMessage({ id: 'users.list-block.table.attributes' })}
        </Button>
        <VerticalSeparator />
        <UserMoreButton>
          <SelectUserSettings
            setUsersState={setUsersState}
            registered={registered}
            externalId={external_id}
            banData={ban}
            userId={id}
          />
        </UserMoreButton>
      </ManageUserWrapper>
    ],
    extra: collapseBanInfo[id] && (
      <BanInfoRow>
        <BanInfoIndent />
        <BanInfoWrapper>
          <BanInfoTitle>{intl.messages['users.list-block.table.ban-info-row.title']}</BanInfoTitle>
          <FlexParentBlock>
            <BanTitlesBlock>
              <BanInfoSubtitle>
                {intl.messages['users.list-block.table.ban-info-row.date']}
              </BanInfoSubtitle>
              <BanInfoSubtitle>
                {intl.messages['users.list-block.table.ban-info-row.reason']}
              </BanInfoSubtitle>
            </BanTitlesBlock>
            <div>
              <BanInfoDescription>
                {ban?.date_to
                  ? moment(ban?.date_to).format('L LT')
                  : intl.messages['users.list-block.table.ban-info-row.date.permanent']}
              </BanInfoDescription>
              <BanInfoDescription>{ban?.reason}</BanInfoDescription>
            </div>
          </FlexParentBlock>
        </BanInfoWrapper>
      </BanInfoRow>
    )
  });

  const NoDataContent = useMemo(() => {
    return (
      <NoDataContentBox>
        <Title>
          {
            intl.messages[
              error ? 'users.list-block.table.error.title' : 'users.list-block.table.no-data.title'
            ]
          }
        </Title>
        <NoDataSubtitle>
          {
            intl.messages[
              error
                ? 'users.list-block.table.error.subtitle'
                : 'users.list-block.table.no-data.title'
            ]
          }
        </NoDataSubtitle>
      </NoDataContentBox>
    );
  }, [error]);

  return (
    <Table
      fetching={fetching}
      sortStatuses={sortStatuses}
      noDataContent={NoDataContent}
      head={tableHeadProps}
      renderRowDeps={[collapseBanInfo]}
      renderRow={rowComponents}
      rowStyles={getRowStyles}
      tableData={tableData}
    />
  );
};
